import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Download as DownloadIcon, InsertDriveFileOutlined } from "@mui/icons-material";
import { useGetOne } from "react-admin";
import { ResourceName, type DocumentRead } from "@/types";
import { ButtonIconLoadingPosition, UIComponentSize } from "@/types/shared/uiComponents";
import Button from "@/components/ui/Button";
import { PdfViewer } from "@/components/common/PdfViewer";
import { useDownloadDocument } from "@/hooks/useDownloadDocument";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { fetchDocumentResource } from "@/providers/dataProvider/documentDownload";
import { composeDocumentLabel } from "@/utils/formatters/documentLabel";
import { getFileExtension, triggerBlobDownload } from "@/utils/formatters/fileUtils";

type PreviewableDoc = Pick<DocumentRead, "name" | "object_metadata"> | null | undefined;

const mimeOf = (doc: PreviewableDoc) => (doc?.object_metadata?.mime_type || "").toLowerCase();
const extOf = (doc: PreviewableDoc) => (doc ? getFileExtension(doc.name).toLowerCase() : "");

/**
 * Whether a document can render inline in {@link PdfViewer}. Prefers the stored
 * MIME type; falls back to the filename extension.
 */
export const isPreviewablePdf = (doc: PreviewableDoc): boolean => {
  if (!doc) return false;
  const mime = mimeOf(doc);
  if (mime) return mime.includes("pdf");
  return extOf(doc) === ".pdf";
};

// Browser-native raster image types. TIFF is deliberately excluded — browsers
// don't render it inline, so it takes the download fallback (#6506).
const IMAGE_EXTS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp"];

/** Whether a document is an inline-renderable image (PNG/JPG/GIF/WebP/BMP, not TIFF). */
export const isPreviewableImage = (doc: PreviewableDoc): boolean => {
  if (!doc) return false;
  const mime = mimeOf(doc);
  if (mime.includes("tiff")) return false;
  if (mime.startsWith("image/")) return true;
  if (mime) return false;
  return IMAGE_EXTS.includes(extOf(doc));
};

/** Whether a document is plain text that can render inline. */
export const isPreviewableText = (doc: PreviewableDoc): boolean => {
  if (!doc) return false;
  const mime = mimeOf(doc);
  if (mime) return mime === "text/plain";
  return extOf(doc) === ".txt";
};

type PreviewKind = "pdf" | "image" | "text" | "other";

const previewKind = (doc: PreviewableDoc): PreviewKind => {
  if (isPreviewablePdf(doc)) return "pdf";
  if (isPreviewableImage(doc)) return "image";
  if (isPreviewableText(doc)) return "text";
  return "other";
};

interface AttachmentPreviewProps {
  documentId: string;
}

/**
 * Single-document preview for the right drawer (ADR-087 D4, extended for #6506).
 * PDFs, images, and plain text render inline; every other type (Office, TIFF,
 * email, …) falls back to a clear download action — never a blank pane or a
 * "Failed to render" error. The blob is only fetched inline for the renderable
 * kinds; a spreadsheet is not downloaded just to draw a "can't preview" pane.
 */
export const AttachmentPreview = ({ documentId }: AttachmentPreviewProps) => {
  const { data: doc, isLoading: docLoading } = useGetOne<DocumentRead>(
    ResourceName.DOCUMENTS,
    { id: documentId },
    { enabled: !!documentId }
  );

  const kind = previewKind(doc);
  const inline = kind !== "other";

  // Only pull the blob inline for renderable kinds; "" disables the fetch (the
  // hook early-returns), so non-renderable types download on demand instead.
  const { data: blob, isLoading: isDownloading, error } = useDownloadDocument(inline ? documentId : "");

  if (docLoading) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (kind === "pdf") {
    return (
      <Box height="100%" display="flex" flexDirection="column">
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          <PdfViewer source={blob} isLoading={isDownloading} error={error} singlePageMode />
        </Box>
      </Box>
    );
  }

  if (kind === "image") {
    return <ImagePreview doc={doc} blob={blob} isLoading={isDownloading} error={error} />;
  }

  if (kind === "text") {
    return <TextPreview doc={doc} blob={blob} isLoading={isDownloading} error={error} />;
  }

  return <NonPdfFallback doc={doc} />;
};

/** Inline image preview from the downloaded blob (object URL, revoked on change). */
const ImagePreview = ({
  doc,
  blob,
  isLoading,
  error,
}: {
  doc: DocumentRead | undefined;
  blob: Blob | null;
  isLoading: boolean;
  error: string | null;
}) => {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);

  if (isLoading) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !url) return <NonPdfFallback doc={doc} />;

  return (
    // data-clarity-mask: attachment bodies are PHI (carrier letters, EOBs) — keep
    // the rendered content out of Microsoft Clarity session replay (#6506 PHI).
    <Box
      data-clarity-mask="true"
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ overflow: "auto", p: 1 }}
    >
      <Box
        component="img"
        src={url}
        alt={composeDocumentLabel(doc)}
        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
      />
    </Box>
  );
};

// Cap how much of a text attachment we decode + render inline: enough to preview,
// bounded so a large .txt can't lock the main thread (or flood Clarity). The full
// file is always one click away via the download fallback.
const TEXT_PREVIEW_MAX_BYTES = 256 * 1024;

/** Inline plain-text preview from the downloaded blob (first 256 KB). */
const TextPreview = ({
  doc,
  blob,
  isLoading,
  error,
}: {
  doc: DocumentRead | undefined;
  blob: Blob | null;
  isLoading: boolean;
  error: string | null;
}) => {
  // `loaded` (not `!text`) is the readiness signal — a 0-byte file resolves to
  // "" and would otherwise keep the spinner up forever.
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [readError, setReadError] = useState(false);
  const truncated = !!blob && blob.size > TEXT_PREVIEW_MAX_BYTES;

  useEffect(() => {
    let cancelled = false;
    if (!blob) return;
    setLoaded(false);
    setReadError(false);
    // Only decode the first slice — bounds CPU/DOM and the Clarity exposure.
    const slice = blob.slice(0, TEXT_PREVIEW_MAX_BYTES);
    // FileReader (not blob.text()) for broad environment support — reads as UTF-8
    // and reports errors through onerror.
    const reader = new FileReader();
    reader.onload = () => {
      if (cancelled) return;
      setText(String(reader.result ?? ""));
      setLoaded(true);
    };
    reader.onerror = () => {
      if (!cancelled) setReadError(true);
    };
    try {
      reader.readAsText(slice);
    } catch {
      if (!cancelled) setReadError(true);
    }
    return () => {
      cancelled = true;
      reader.abort();
    };
  }, [blob]);

  if (isLoading || (!loaded && !error && !readError)) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }
  // Error is not a dead end (AC4 / ADR-087 D4 "never a blank pane"): fall through
  // to the download action, matching ImagePreview.
  if (error || readError) return <NonPdfFallback doc={doc} />;

  return (
    // data-clarity-mask: see ImagePreview — attachment text is PHI.
    <Box data-clarity-mask="true" height="100%" sx={{ overflow: "auto", p: 1.5 }}>
      {truncated && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Showing the first 256 KB — download for the full file.
        </Typography>
      )}
      <Typography
        component="pre"
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace", m: 0 }}
      >
        {text}
      </Typography>
    </Box>
  );
};

/**
 * Non-renderable fallback: file identity plus a download action. No inline render
 * is attempted, so the user never sees a broken viewer for a legitimate
 * attachment (an XLS/XLSX claims register, a .docx letter, a .msg email).
 */
const NonPdfFallback = ({ doc }: { doc: DocumentRead | undefined }) => {
  const [downloading, setDownloading] = useState(false);
  const notifyError = useRichApiErrorNotify();

  const handleDownload = async () => {
    if (!doc?.id) return;
    setDownloading(true);
    try {
      // Partner-scoped, authenticated headers are built centrally so this raw
      // download can't silently drop X-Partner-ID (#5350).
      const response = await fetchDocumentResource(`${getPartnerAwareApiUrl()}/documents/${doc.id}/download`);
      triggerBlobDownload(await response.blob(), composeDocumentLabel(doc));
    } catch (err) {
      notifyError(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    // data-clarity-mask: the filename is user-supplied and may carry PHI (#6506).
    <Box
      data-clarity-mask="true"
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={1.5}
      p={4}
      textAlign="center"
    >
      <InsertDriveFileOutlined sx={{ fontSize: 48, color: "text.secondary" }} />
      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
        {composeDocumentLabel(doc)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This file type can’t be previewed here. Download it to open in the right app.
      </Typography>
      <Button
        size={UIComponentSize.Small}
        startIcon={<DownloadIcon />}
        loading={downloading}
        loadingPosition={ButtonIconLoadingPosition.Start}
        onClick={handleDownload}
        disabled={!doc?.id}
      >
        {downloading ? "Downloading…" : "Download"}
      </Button>
    </Box>
  );
};
